import { IAgencyAlert, IAgencyDigest, IClientBaseline, IHistoryProgress, movedTheirCondition, notCountedInCoverage } from './agencyDigest';
import { monthDay } from './agencyChartTheme';
import type { IFamilyParticipation } from './agencyValue';
import { RECENT_WEEKS } from './agencyThresholds';
import { buildCaseloadWellbeing, DOMAIN_LABEL, IClientWellbeing, UNPLACED_INCIDENT_SENTENCE } from './agencyDomains';

/**
 * What Bea says about each panel, worked out from the same digest the panel draws.
 *
 * Kept here rather than written into the components for two reasons. A sentence built from the data
 * has to be able to say "nothing to report" as fluently as it says "three clients moved", and that
 * is logic, not markup. And a line in Bea's voice is a claim about the caseload, so it belongs
 * somewhere it can be tested: every branch below has a test, because the failure mode of generated
 * prose is a sentence that is confidently wrong about a person.
 *
 * The rule the whole tab is built on applies here too. These describe what is on the record. None of
 * them says why anything happened, or what anybody should do about it beyond looking.
 */

const plural = (count: number, one: string, many: string): string => (count === 1 ? one : many);

/**
 * A count and the noun it governs are not always the same number.
 *
 * "1 of 4 clients is missing a code status" pluralizes the NOUN against the total and the VERB
 * against the count, and one call cannot get both right. Passing a single number to a helper that
 * emits both is what produced "1 of 4 client is missing", so the two agreements are kept apart here.
 */
const outOf = (params: { count: number; total: number; verb: [string, string] }): string => {
    const { count, total, verb } = params;
    return `${count} of ${total} ${plural(total, 'client', 'clients')} ${plural(count, verb[0], verb[1])}`;
};

/** Small numbers read as words in running prose: "the other 1" is not a sentence anybody writes. */
const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const inWords = (count: number): string => WORDS[count] ?? String(count);

/** inWords emits lowercase, so a count that opens a sentence has to be lifted. */
const sentenceStart = (word: string): string => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * The counts every branch of describeCaseload selects on.
 *
 * Lifted out because the function is branch selection over these six numbers rather than
 * arithmetic, and reading it that way is what makes a missing branch visible.
 */
interface ICaseloadCounts {
    total: number;
    declining: number;
    settled: number;
    notYetKnown: number;
    incidentAmongDeclining: number;
    /**
     * A client with an incident who is not declining had it earlier in the window, since a recent
     * one forces the declining reading. Counted apart so that "of whom" stays a true subset of the
     * clients it is hung off.
     */
    incidentElsewhere: number;
}

const readCaseload = (digest: IAgencyDigest): ICaseloadCounts => {
    const decliningIds = new Set(
        digest.baselines.filter(baseline => baseline.standing === 'above').map(baseline => baseline.careMapId)
    );
    const incidentIds = new Set(
        digest.alerts.filter(alert => alert.kind === 'incident').map(alert => alert.careMapId)
    );
    const incidentAmongDeclining = [...incidentIds].filter(careMapId => decliningIds.has(careMapId)).length;

    return {
        total: digest.baselines.length,
        declining: decliningIds.size,
        settled: digest.baselines.filter(baseline => baseline.standing === 'settled').length,
        notYetKnown: digest.baselines.filter(baseline => baseline.standing === 'unknown').length,
        incidentAmongDeclining,
        incidentElsewhere: incidentIds.size - incidentAmongDeclining
    };
};

/**
 * The clauses describeCaseload hangs off its lead sentence, each empty when its group is.
 *
 * Separated from the branch selection because they are the part with the trap in it: every one of
 * them names a group, those groups overlap, and read side by side they can describe more people
 * than the caseload holds. Keeping them together is what makes that readable as one problem.
 */
const caseloadTails = (counts: ICaseloadCounts): { unread: string; amongDeclining: string; elsewhere: string; settled: string } => {
    const { settled, notYetKnown, incidentAmongDeclining, incidentElsewhere } = counts;

    return {
        // Carried into every branch, so a caseload that is mostly unread never produces a sentence
        // that sounds like a verdict on all of it.
        unread: notYetKnown > 0
            ? ` I do not have enough to compare ${inWords(notYetKnown)} of them against their own baseline yet.`
            : '',
        // "of whom", not a clause of its own: these sets overlap, and a client who is both off
        // baseline and had a fall is in each. Side by side the counts read as a partition of the
        // caseload and can add up past it, which is a sentence that is wrong about real people.
        // Only clients who ARE declining can hang off "of whom"; an incident on anybody else needs
        // its own clause, marked as an overlay for the same reason.
        amongDeclining: incidentAmongDeclining > 0
            ? `, ${plural(incidentAmongDeclining, 'one of whom', `${incidentAmongDeclining} of whom`)} had something happen`
            : '',
        elsewhere: incidentElsewhere > 0
            ? (counts.total === 1
                ? ' Something happened to them earlier in the window.'
                : ` ${plural(incidentElsewhere, 'One of them', `${sentenceStart(inWords(incidentElsewhere))} of them`)} had something happen earlier in the window.`)
            : '',
        settled: settled > 0
            ? ` ${plural(settled, 'One client has', `${sentenceStart(inWords(settled))} clients have`)} come back down and ${plural(settled, 'reads', 'read')} settled.`
            : ''
    };
};

/**
 * The clause an all-clear sentence needs when clients were dropped from the digest entirely.
 *
 * Deliberately worded "at all", because this tab already has two other absences a reader could
 * confuse it with: a client there is not enough to compare yet, and a client kept off the strip
 * for an incident. Both of those were read; this one was not.
 *
 * Empty when nothing was dropped, so the ordinary case reads exactly as it always did.
 */
export const describeUnreadable = (unreadableCount: number, followsACount = false): string => {
    if (unreadableCount === 0) {
        return '';
    }

    // "Another" only where a count was just stated, which would otherwise read as the same people
    // counted twice: "The two clients I can read are sitting at their own baseline. Two clients
    // could not be read at all." Everywhere else the tail follows a sentence with no number, where
    // "another" has nothing to count from.
    if (followsACount) {
        return unreadableCount === 1
            ? ' Another client could not be read at all.'
            : ` Another ${inWords(unreadableCount)} clients could not be read at all.`;
    }

    return unreadableCount === 1
        ? ' One client could not be read at all.'
        : ` ${sentenceStart(inWords(unreadableCount))} clients could not be read at all.`;
};

/** How many clients are off their baseline, how many had something happen, and who is quiet. */
export const describeCaseload = (digest: IAgencyDigest): string => {
    const { total, declining, settled, notYetKnown, incidentAmongDeclining, incidentElsewhere } = readCaseload(digest);
    if (total === 0) {
        return digest.unreadableCount > 0
            ? 'I could not read any of the clients on this book, so there is nothing for me to report.'
            : 'No clients on the book yet, so there is nothing for me to read.';
    }

    const read = total - notYetKnown;
    const anyIncident = incidentAmongDeclining + incidentElsewhere;
    const tail = caseloadTails({ total, declining, settled, notYetKnown, incidentAmongDeclining, incidentElsewhere });

    // Nothing with enough behind it to compare. "Nobody is off their own baseline" would be a claim
    // about people there is no baseline for, and it is the most reassuring sentence on the tab.
    // The incident clause has to survive into here: an unreadable caseload can still contain
    // somebody who fell, and a remainder clause would otherwise name a group of nobody.
    if (read === 0) {
        const fellTail = anyIncident > 0
            ? (total === 1
                ? ', though something happened to them'
                : `, though ${plural(anyIncident, 'one of them', `${inWords(anyIncident)} of them`)} had something happen`)
            : '';
        return total === 1
            ? `There is not enough yet to compare your one client against their own baseline${fellTail}.`
            : `There is not enough yet to compare any of your ${total} clients against their own baseline${fellTail}.`;
    }

    if (declining === 0 && anyIncident === 0) {
        // Settled clients are NOT sitting at their baseline, they are below it, so they cannot be
        // folded into a sentence that says they are and then corrected by the clause after it.
        const holding = read - settled;

        if (holding === 0) {
            return `${plural(settled, 'The one client I can read has', `The ${inWords(settled)} clients I can read have`)}`
                + ` come back down and ${plural(settled, 'reads', 'read')} settled.${tail.unread}`;
        }
        if (notYetKnown === 0 && settled === 0) {
            // "all N clients" and "the one client on your book" both count the BOOK, so with
            // anybody unreadable they contradict the tail that follows them: one client on the
            // book, plus one more that could not be read, is two. Scoped to the readable set
            // instead, matching how the sibling branches here already hedge.
            if (digest.unreadableCount > 0) {
                return `${plural(total, 'The one client I can read is', `The ${inWords(total)} clients I can read are`)}`
                    + ` sitting at their own baseline.${describeUnreadable(digest.unreadableCount, true)}`;
            }
            return total === 1
                ? 'I read the one client on your book, and they are sitting at their own baseline.'
                : `I read all ${total} clients and nobody is off their own baseline right now.`;
        }
        return `${plural(holding, 'One client', `${sentenceStart(inWords(holding))} clients`)} I can read`
            + ` ${plural(holding, 'is', 'are')} sitting at their own baseline.${tail.settled}${tail.unread}`;
    }

    if (declining === 0) {
        const lead = settled > 0
            ? 'Nobody I can read has picked up against their own baseline.'
            : 'Nobody I can read is off their own baseline.';
        return `${lead}${tail.elsewhere}${tail.settled}${tail.unread}`;
    }

    return `${outOf({ count: declining, total, verb: ['is', 'are'] })} off their own baseline${tail.amongDeclining}.`
        + `${tail.elsewhere}${tail.settled}${tail.unread}`;
};

/** The client at the top of the attention list, and what moved for them. */
export const describeAttention = (digest: IAgencyDigest): string => {
    const all: IClientWellbeing[] = buildCaseloadWellbeing({ digest });
    const scored = all.filter(wellbeing => wellbeing.overall !== 'unscored');
    const unscored = all.length - scored.length;
    const worst = scored[0];

    // Nobody to compare is not a calm caseload, and saying every part of daily life is where it
    // usually does would be a verdict about people the tab has just declared it cannot read.
    if (all.length > 0 && scored.length === 0) {
        return (all.length === 1
            ? 'There is not enough yet to compare your one client against their own baseline.'
            : `There is not enough yet to compare any of your ${all.length} clients against their own baseline.`)
            + describeUnreadable(digest.unreadableCount, true);
    }

    if (worst && worst.moved.length === 0 && worst.unplacedIncidents.length > 0) {
        return `I would start with ${worst.clientName}, at ${worst.score} out of 100. ${UNPLACED_INCIDENT_SENTENCE}`;
    }

    if (!worst || worst.moved.length === 0) {
        if (unscored > 0) {
            return 'Nobody I can compare stands out this week.'
                + ` ${plural(unscored, 'One client', `${sentenceStart(inWords(unscored))} clients`)} cannot be compared yet.`
                + describeUnreadable(digest.unreadableCount, true);
        }
        return 'Nobody stands out this week. Every part of daily life is sitting where it usually does.'
            + describeUnreadable(digest.unreadableCount);
    }

    const named = worst.moved.slice(0, 2).map(domain => DOMAIN_LABEL[domain].toLowerCase());
    const list = named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
    const more = worst.moved.length - named.length;
    const tail = more > 0 ? `, and ${more} more` : '';

    const start = `I would start with ${worst.clientName}: ${list}${tail} ${plural(worst.moved.length, 'has', 'have')} moved, which puts them at ${worst.score} out of 100.`;
    return worst.unplacedIncidents.length > 0 ? `${start} ${UNPLACED_INCIDENT_SENTENCE}` : start;
};

/** The counts the sentence under the deviation strip selects on, read off the same predicate. */
const readBaselines = (baselines: IClientBaseline[]) => {
    const picking = baselines.filter(baseline => baseline.conditionStanding === 'above').length;
    const settling = baselines.filter(baseline => movedTheirCondition(baseline) && baseline.conditionStanding === 'settled').length;

    return {
        picking,
        settling,
        notYetKnown: baselines.filter(baseline => baseline.conditionStanding === 'unknown').length,
        /**
         * Clients the strip leaves out because they were flagged for something that happened and
         * nothing else about them put them on it anyway. Left unsaid, every sentence below reports a
         * caseload where nothing has moved, which about somebody who fell last week is the worst
         * thing this panel can say.
         */
        withheldForIncident: baselines.filter(baseline => {
            return baseline.hasRecentIncident && !movedTheirCondition(baseline);
        }).length
    };
};

/**
 * The clauses the strip's sentences end with, one per kind of client the strip leaves out.
 *
 * Mirrors caseloadTails: built together so no branch below can quietly omit one, and so the three
 * absences keep separate wording. `incident` says nothing about the rest of the record on purpose,
 * because the group includes a client with less raised than usual, for whom "nothing moved" would be
 * as untrue as "more raised than usual".
 */
const baselineTails = (params: {
    notYetKnown: number;
    withheldForIncident: number;
    unreadableCount: number;
    narrowed: boolean;
}) => {
    const { notYetKnown, withheldForIncident, unreadableCount, narrowed } = params;

    return {
        // Follows incident and comparison clauses that both state counts, so it counts from them.
        unreadableAfterCounts: narrowed ? '' : describeUnreadable(unreadableCount, true),
        incident: withheldForIncident > 0
            ? ` ${plural(withheldForIncident, 'One client is', `${sentenceStart(inWords(withheldForIncident))} clients are`)}`
                + ' on the attention list for something that happened.'
            : '',
        cannotCompare: notYetKnown > 0
            ? ` ${plural(notYetKnown, 'One client does', `${sentenceStart(inWords(notYetKnown))} clients do`)} not have enough to compare yet.`
            : '',
        // A narrowed strip is already explicitly about a selection, so it needs no caveat: the
        // reader asked for a subset and is being answered about that subset.
        unreadable: narrowed ? '' : describeUnreadable(unreadableCount)
    };
};

export const describeBaselines = (params: {
    baselines: IClientBaseline[];
    narrowed: boolean;
    unreadableCount: number;
}): string => {
    const { baselines, narrowed, unreadableCount } = params;

    if (baselines.length === 0) {
        if (narrowed) {
            return 'Nothing in this selection to compare against a baseline.';
        }
        // "No clients to compare" is false when there are clients and none of them could be read,
        // and the strip directly above already states that fact. This gives the consequence
        // rather than restating it, so the two lines read as one thought instead of a fault.
        return unreadableCount > 0
            ? 'There is nothing to compare until some of these clients can be read.'
            : 'No clients to compare against a baseline yet.';
    }

    const { picking, settling, notYetKnown, withheldForIncident } = readBaselines(baselines);
    const tail = baselineTails({ notYetKnown, withheldForIncident, unreadableCount, narrowed });

    // A flat strip because nobody has moved and a flat strip because nobody can be measured look
    // identical. Only the first of them is a good week.
    // The incident clause survives into here. Reading the CONDITION rather than the standing means a
    // client who fell can still be unmeasurable, so a caseload with nothing to compare can contain
    // somebody who needs a visit today.
    // "No client has enough to compare" is a claim about clients nobody read as much as the branches
    // below are: we do not know what record they have.
    if (notYetKnown === baselines.length) {
        return (narrowed
            ? 'Nothing in this selection has enough to compare against a baseline yet.'
            : 'No client has enough to compare against their own baseline yet.')
            + tail.incident
            + (tail.incident ? tail.unreadableAfterCounts : tail.unreadable);
    }

    if (picking === 0 && settling === 0) {
        if (narrowed) {
            return `No client in this selection has moved against their own baseline.${tail.incident}${tail.cannotCompare}`;
        }
        // Subtracting the withheld as well as the unmeasured. Counting them among those holding
        // asserts a client who fell is at their baseline, and then corrects it in the clause
        // after, which is the construction describeCaseload exists to avoid.
        const holding = baselines.length - notYetKnown - withheldForIncident;
        if (notYetKnown === 0 && withheldForIncident === 0) {
            return `Everybody is holding at their own baseline across the last ${RECENT_WEEKS} weeks.`
                + ` A flat strip is a good week.${tail.unreadable}`;
        }
        // Universal like the branch above it, and unlike the hedged one below, so it needs the
        // same caveat: "no client has moved" is a claim about clients nobody read.
        if (holding === 0) {
            return `No client has moved against their own earlier weeks.`
                + `${tail.incident}${tail.cannotCompare}${tail.unreadableAfterCounts}`;
        }
        return `${plural(holding, 'One client', `${sentenceStart(inWords(holding))} clients`)}`
            + ` ${plural(holding, 'is', 'are')} holding at their own baseline across the last ${RECENT_WEEKS} weeks.`
            + `${tail.incident}${tail.cannotCompare}`;
    }

    // The first clause always carries the noun, because it is the one the lead-in runs into. Written
    // the other way round a settling-only caseload read "Against their own earlier weeks, 2 have
    // less raised than usual", with nothing saying two of what.
    const parts: string[] = [];
    if (picking > 0) {
        parts.push(`${picking} ${plural(picking, 'client has', 'clients have')} more raised about them than usual`);
    }
    if (settling > 0) {
        const noun = picking > 0 ? plural(settling, 'has', 'have') : plural(settling, 'client has', 'clients have');
        parts.push(`${settling} ${noun} less raised about them than usual`);
    }

    return `Against their own earlier weeks, ${parts.join(' and ')}.${tail.incident}${tail.cannotCompare}`
        + ' Each bar is that client against themselves, never against the caseload.';
};

/**
 * What the changes in this window are, and how many of them the list actually shows.
 *
 * Counted over everything the window holds rather than over the rows on screen, which is why it
 * takes the whole set and a shown count rather than the rows themselves. The list is capped, so a
 * sentence about "these" was describing a slice: it told a dispatcher that the two incidents they
 * could see were the ones to read first while three more sat unlisted and the tile above the card
 * said five.
 */
export const describeChanges = (params: { all: IAgencyAlert[]; shownCount: number; unreadableCount: number }): string => {
    const { all, shownCount, unreadableCount } = params;

    if (all.length === 0) {
        // Nothing recorded for the clients in this digest. Said flat, a reader takes it for the
        // whole book, which is the one reading that is wrong when somebody was never read.
        return `Nothing new has been recorded in this window.${describeUnreadable(unreadableCount)}`;
    }

    const incidents = all.filter(alert => alert.kind === 'incident').length;
    const unlisted = Math.max(0, all.length - shownCount);
    const moreTail = unlisted > 0
        ? ` ${plural(unlisted, 'One more is', `${inWords(unlisted)} more are`)} in the window but not on this list.`
        : '';

    if (incidents === 0) {
        return `${all.length} ${plural(all.length, 'change is', 'changes are')} in this window, most recent first. None of them is an incident, which is the good version of a busy list.${moreTail}`;
    }

    return `${plural(incidents, 'One of these is something that already happened, so that is the one', `${incidents} of these are things that already happened, so they are the ones`)} to read first.${moreTail}`;
};

/** Which part of the record is furthest from complete. */
export const describeCoverage = (params: { digest: IAgencyDigest }): string => {
    const { digest } = params;
    const { coverage } = digest;
    if (coverage.clientCount === 0) {
        // Zero readable clients means either an empty book or a book this organization may not read
        // privately, and those are different facts to hand somebody. Both counts, because they
        // catch the same thing by different routes and neither alone is safe: `unreadableCount`
        // is what the fan-out classified, while `unknownCount` catches a client that reached the
        // digest without coverage anyway, which happens whenever the care map list has moved on
        // since that fan-out ran.
        return notCountedInCoverage(digest) > 0
            ? 'I cannot read what is on file for any of these clients, so there is nothing for me to check.'
            : 'Nothing on file to report on yet.';
    }

    const gaps = [
        { label: 'a confirmed code status', missing: coverage.clientCount - coverage.codeStatusConfirmed },
        { label: 'insurance on file', missing: coverage.clientCount - coverage.insuranceOnFile },
        { label: 'an identity document', missing: coverage.clientCount - coverage.identityOnFile }
    ].sort((a, b) => b.missing - a.missing);

    const worst = gaps[0];
    if (worst.missing === 0) {
        // "Every client" has to mean every client the organization can read, or the one case this
        // sentence is for, a book with nothing outstanding, is also the case where it is a claim
        // about people nobody has looked at.
        const unread = notCountedInCoverage(digest);
        if (unread > 0) {
            return 'Every client I can read has all three on file.'
                + ` ${plural(unread, 'The one client', `The ${inWords(unread)} clients`)} I could not read at all may not.`;
        }
        return 'Every client has all three on file. That is the version of this panel nobody expects to see.';
    }

    // A tie is common on a small book, and naming one of two equal gaps as "the largest" is a claim
    // the data does not support.
    const tied = gaps.filter(gap => gap.missing === worst.missing).length > 1;
    const rank = tied ? 'one of the largest gaps' : 'the largest gap';

    const counted = outOf({ count: worst.missing, total: coverage.clientCount, verb: ['is', 'are'] });

    return `${counted} missing ${worst.label}, which is ${rank}. Open a line to see exactly who.`;
};

/**
 * What a panel waiting on history says in place of its figures. The panel's own heading already
 * names what will appear; this says how far there is to go and the earliest it could arrive.
 */
export const describeHistoryWait = (history: IHistoryProgress, { fillsIn = 'This fills in' }: { fillsIn?: string } = {}): string => {
    const { findingCount, findingsNeeded, weeksWithFindings, weeksNeeded, windowWeeks, weeksToWait, earliestWeekStartsOn } = history;

    // Names the caseload because this also stands in for one client's diagram, where a bare count
    // would read as that client's own history.
    const target = `${fillsIn} once the whole caseload has at least ${findingsNeeded} findings in its last ${windowWeeks} weeks, spread over ${weeksNeeded} different weeks.`;
    const soFar = findingCount === 0
        ? 'So far it has none.'
        : `So far it has ${findingCount} ${plural(findingCount, 'finding', 'findings')} in ${weeksWithFindings} ${plural(weeksWithFindings, 'week', 'weeks')}.`;

    if (weeksToWait === 0) {
        return `${target} ${soFar} That could happen this week.`;
    }
    if (weeksToWait === 1) {
        return `${target} ${soFar} The earliest that can happen is next week.`;
    }
    if (earliestWeekStartsOn) {
        return `${target} ${soFar} The earliest that can happen is the week of ${monthDay(earliestWeekStartsOn)}.`;
    }
    return `${target} ${soFar}`;
};

/**
 * What the Outcomes Report says under the figures it can show about the ones still waiting. Names
 * them, because the figures beside it are real and "this" would read as all of them.
 */
export const describeOutcomesWait = (params: { waiting: string[]; history: IHistoryProgress }): string => {
    const { waiting, history } = params;
    const comparison = `${waiting.join(' and ')} ${plural(waiting.length, 'compares', 'compare')} each client with their own earlier weeks.`;
    return `${comparison} ${describeHistoryWait(history, { fillsIn: plural(waiting.length, 'It fills in', 'They fill in') })}`;
};

/** Bea's line under the family participation figures. */
export const describeParticipation = (params: { participation: IFamilyParticipation; unreadableCount: number }): string => {
    const { participation, unreadableCount } = params;
    const { clientCount, participatingCount, quietCount } = participation;

    // With nobody on the book, "nobody is quiet" is vacuously true and reads as the best week possible.
    if (clientCount === 0) {
        return unreadableCount > 0
            ? 'I could not read any of the clients on this book, so there is nothing for me to report.'
            : 'No clients on the book yet, so there is nothing for me to read.';
    }
    if (quietCount === 0) {
        return `Every client I can read had a family observation this week, which is as high as this goes.${describeUnreadable(unreadableCount)}`;
    }
    return `${participatingCount} of ${clientCount} families put something on the record this week. The ${quietCount === 1 ? 'other one is' : `other ${quietCount} are`} where I would start: a quiet family is usually an onboarding gap rather than a quiet household.`;
};
